var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import './Stepper.styles.scss';
import { Icon } from '@gerdau/hefesto';
import { InputWrapper } from '@gerdau/hefesto/InputWrapper';
var EMappedInputEventCodes;
(function (EMappedInputEventCodes) {
    EMappedInputEventCodes["ARROW_DOWN"] = "ArrowDown";
    EMappedInputEventCodes["ARROW_UP"] = "ArrowUp";
})(EMappedInputEventCodes || (EMappedInputEventCodes = {}));
var Stepper = function (_a) {
    var disabled = _a.disabled, helperText = _a.helperText, _b = _a.hideIncreaseDecreaseButtons, hideIncreaseDecreaseButtons = _b === void 0 ? false : _b, label = _a.label, _c = _a.hideLabel, hideLabel = _c === void 0 ? false : _c, maxLength = _a.maxLength, minLength = _a.minLength, _d = _a.multiple, multiple = _d === void 0 ? 1 : _d, name = _a.name, onBlur = _a.onBlur, onChange = _a.onChange, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, onKeyUp = _a.onKeyUp, readOnly = _a.readOnly, supportMessage = _a.supportMessage, _e = _a.tabIndex, tabIndex = _e === void 0 ? 0 : _e, value = _a.value, variant = _a.variant, helperTextTooltipPlacement = _a.helperTextTooltipPlacement, props = __rest(_a, ["disabled", "helperText", "hideIncreaseDecreaseButtons", "label", "hideLabel", "maxLength", "minLength", "multiple", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onKeyUp", "readOnly", "supportMessage", "tabIndex", "value", "variant", "helperTextTooltipPlacement"]);
    var inputRef = useRef(null);
    var onChangeInput = function (event) {
        if (/^\d+(\.\d{1,3})?$/.test(Number(event.target.value).toString())) {
            !!onChange && onChange(event);
        }
    };
    var onIncreaseValue = function (event) {
        var _a;
        var newValue = Number((_a = value === null || value === void 0 ? void 0 : value.toString().replace(',', '.')) !== null && _a !== void 0 ? _a : 0) + multiple;
        if (maxLength !== undefined && newValue > maxLength) {
            onChangeInput(getCustomEvent(event, maxLength));
        }
        else if (minLength !== undefined && newValue < minLength) {
            onChangeInput(getCustomEvent(event, minLength));
        }
        else {
            onChangeInput(getCustomEvent(event, newValue));
        }
    };
    var onDecreaseValue = function (event) {
        var _a;
        var newValue = Number((_a = value === null || value === void 0 ? void 0 : value.toString().replace(',', '.')) !== null && _a !== void 0 ? _a : 0) - multiple;
        if (minLength !== undefined && newValue < minLength) {
            onChangeInput(getCustomEvent(event, minLength));
        }
        else {
            onChangeInput(getCustomEvent(event, newValue));
        }
    };
    var onInputKeyDown = function (event) {
        if (!Object.values(EMappedInputEventCodes).find(function (code) { return code === event.code; })) {
            !!onKeyDown && onKeyDown(event);
            return;
        }
        event.preventDefault();
        !!onKeyDown && onKeyDown(event);
        switch (event.code) {
            case EMappedInputEventCodes.ARROW_UP: {
                onIncreaseValue(event);
                return;
            }
            case EMappedInputEventCodes.ARROW_DOWN: {
                onDecreaseValue(event);
                return;
            }
            default: {
                return;
            }
        }
    };
    var getCustomEvent = function (event, newInputValue) {
        var _a;
        var currentTarget = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.cloneNode(true);
        event = Object.create(event, {
            target: { value: __assign(__assign({}, currentTarget), { value: newInputValue }) },
            currentTarget: { value: currentTarget },
        });
        return event;
    };
    var onInputBlur = function (event) {
        if (maxLength !== undefined && Number(value) > maxLength) {
            onChangeInput(getCustomEvent(event, maxLength));
        }
        else if (minLength !== undefined && Number(value) < minLength) {
            onChangeInput(getCustomEvent(event, minLength));
        }
        else {
            !!onBlur && onBlur(event);
        }
    };
    var getContainerClassList = function () {
        var classList = ['hefesto-stepper'];
        if (variant) {
            classList.push("hefesto-stepper--".concat(variant));
        }
        if (disabled) {
            classList.push("hefesto-stepper--disabled");
        }
        return classList.join(' ');
    };
    var containerClassList = getContainerClassList();
    var getInputNumberActionsClassList = function () {
        var classList = ['hefesto-stepper__actions'];
        if (readOnly) {
            classList.push('readonly');
        }
        return classList.join(' ');
    };
    var inputNumberActionsClassList = getInputNumberActionsClassList();
    return (_jsx("div", __assign({ className: containerClassList, "data-testid": "hefesto-stepper-container" }, { children: _jsx(InputWrapper, __assign({ disabled: disabled, helperText: helperText, helperTextTooltipPlacement: helperTextTooltipPlacement, hideLabel: hideLabel, label: label, supportMessage: supportMessage, name: name, variant: variant }, { children: _jsxs("div", __assign({ className: "hefesto-stepper__input-wrapper--children" }, { children: [_jsx("input", __assign({ className: "hefesto-stepper__input-wrapper--input", disabled: disabled, type: "text", id: name, name: name, required: true, ref: inputRef, minLength: minLength, maxLength: maxLength, value: value, onChange: onChangeInput, onKeyDown: onInputKeyDown, onFocus: onFocus, onKeyUp: onKeyUp, onBlur: onInputBlur, tabIndex: tabIndex, readOnly: readOnly, "data-testid": "hefesto-stepper", autoComplete: "off" }, props)), hideIncreaseDecreaseButtons === false && (_jsxs("div", __assign({ className: inputNumberActionsClassList }, { children: [_jsx("button", __assign({ className: "hefesto-stepper__actions--increase", "data-testid": "increase-button", "aria-label": "increase-button", onClick: onIncreaseValue, disabled: disabled ||
                                    readOnly ||
                                    (maxLength !== undefined && Number(value) >= maxLength) }, { children: _jsx("i", { children: _jsx(Icon, { option: "CaretUp", variant: "light" }) }) })), _jsx("button", __assign({ className: "hefesto-stepper__actions--decrease", "data-testid": "decrease-button", "aria-label": "decrease-button", onClick: onDecreaseValue, disabled: disabled ||
                                    readOnly ||
                                    (minLength !== undefined && Number(value) <= minLength) }, { children: _jsx("i", { children: _jsx(Icon, { option: "CaretDown", variant: "light" }) }) }))] })))] })) })) })));
};
export default Stepper;
